import { createWebHistory, createRouter } from "vue-router";
import Home from "@/components/Home.vue";
import AboutView from "@/views/AboutView.vue";
import SettingsView from "@/views/SettingsView.vue";
import InspectionPointView from "@/views/InspectionPointView.vue";
import Login from "@/views/Login.vue";
import EvaluationList from "@/views/evaluation/EvaluationListView.vue";
import EvaluationCourseSelection from "@/views/evaluation/CourseSelectionView.vue";
import UserCourseSelection from "@/views/selection/UserCourseSelectionView.vue";
import UserSessionSelection from "@/views/selection/UserSessionSelectionView.vue";
import EvaluationUserTests from "@/views/evaluation/EvaluationUserTestsView.vue";
import EvaluationDetail from "@/views/evaluation/EvaluationDetailView.vue";
import OwnerCaseSelection from "@/views/selection/OwnerCaseSelectionView.vue";
import store from "@/store";
import { bootstrapLoginFromCltoken } from "@/services/authBootstrap";
import APIService from "@/services/APIService";
import APIServiceRestDB from "@/services/MesskursBackendService";
import {
    getLicenseIdsFromUser,
    getManageableLicenseIds,
    getMesskursInstructorAccessDebug,
    normalizeLicenseContacts,
    resolveId
} from "@/services/messkursAuth";

const routes = [
    {
        path: "/login",
        name: "Login",
        component: Login,
        meta: { public: true }
    },
    {
        path: "/",
        name: "Home",
        component: Home,
    },
    {
        path: "/settings",
        name: "Settings",
        component: SettingsView,
        meta: { requiresAdmin: true }
    },
    {
        path: "/lernen",
        name: "Lernen",
        component: InspectionPointView,
    },
    {
        path: "/ueben",
        name: "Ueben",
        component: InspectionPointView,
    },
    {
        path: "/test",
        name: "Test",
        component: InspectionPointView,
    },
    {
        path: "/inspectionpoints",
        name: "Inspection Points",
        component: InspectionPointView,
        meta: { requiresAdmin: true }
    },
    {
        path: "/selection",
        name: "UserCourseSelection",
        component: UserCourseSelection,
    },
    {
        path: "/selection/cases",
        name: "OwnerCaseSelection",
        component: OwnerCaseSelection,
    },
    {
        path: "/selection/sessions/:courseId",
        name: "UserSessionSelection",
        component: UserSessionSelection,
    },
    {
        path: "/evaluation",
        name: "EvaluationCourseSelection",
        component: EvaluationCourseSelection,
        meta: { requiresInstructor: true }
    },
    {
        path: "/evaluation/list",
        name: "EvaluationList",
        component: EvaluationList,
        meta: { requiresInstructor: true }
    },
    {
        path: "/evaluation/user/:userId",
        name: "EvaluationUserTests",
        component: EvaluationUserTests,
        meta: { requiresInstructor: true }
    },
    {
        path: "/evaluation/user/:userId/test/:sessionId",
        name: "EvaluationDetail",
        component: EvaluationDetail,
        meta: { requiresInstructor: true }
    },
    {
        path: "/about",
        name: "About",
        component: AboutView,
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

async function canAccessInspectionPointsCase(caseId) {
    const user = store.state.user;
    const userId = resolveId(user);
    if (!caseId || !userId) return false;

    const object = await APIServiceRestDB.getObjectWithId(caseId);
    if (!object) return false;

    if (resolveId(object.createdBy) === userId || resolveId(object.owner) === userId) {
        return true;
    }

    const contactsResponse = await APIService.getUserLicenseContacts().catch(() => []);
    const licenseContacts = normalizeLicenseContacts(contactsResponse);
    const scopedOwnerIds = [
        ...getManageableLicenseIds(user, licenseContacts),
        ...getLicenseIdsFromUser(user)
    ];

    return [...new Set(scopedOwnerIds)].includes(resolveId(object.owner));
}

async function hasInstructorAccess() {
    const user = store.state.user;
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'instructor' || user.messkursInstructorAccess === true) {
        return true;
    }

    const contactsResponse = await APIService.getUserLicenseContacts().catch(() => []);
    const licenseContacts = normalizeLicenseContacts(contactsResponse);
    const debug = getMesskursInstructorAccessDebug(user, licenseContacts, store.state.course);

    store.commit('updateUser', {
        ...user,
        messkursInstructorAccess: debug.hasAccess,
        messkursInstructorAccessDebug: debug,
    });

    return debug.hasAccess;
}

router.beforeEach(async (to, from, next) => {
    await bootstrapLoginFromCltoken();

    if (Object.prototype.hasOwnProperty.call(to.query, "cltoken")) {
        const sanitizedQuery = { ...to.query };
        delete sanitizedQuery.cltoken;
        next({
            path: to.path,
            query: sanitizedQuery,
            hash: to.hash,
            replace: true
        });
        return;
    }

    const isLoggedIn = store.getters.isLoggedIn;
    const isPublic = to.matched.some(record => record.meta.public);
    const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
    const requiresInstructor = to.matched.some(record => record.meta.requiresInstructor);
    const userRole = store.state.user?.role;

    if (!isLoggedIn && !isPublic) {
        next({ name: 'Login' });
    } else if (requiresAdmin && to.name === 'Inspection Points' && userRole !== 'admin') {
        if (!to.query.case) {
            next({ name: 'OwnerCaseSelection', query: { ...to.query, mode: 'Inspection Points' } });
            return;
        }

        const hasCaseAccess = await canAccessInspectionPointsCase(to.query.case);
        if (hasCaseAccess) {
            next();
        } else {
            next({ name: 'Home' });
        }
    } else if (requiresAdmin && userRole !== 'admin') {
        next({ name: 'Home' });
    } else if (requiresInstructor && !(await hasInstructorAccess())) {
        next({ name: 'Home' });
    } else {
        next();
    }
});

export default router;
